import React, { useEffect, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import api from '../../services/api';

interface PlatformOverview {
  wineries: number;
  users: number;
  newContactRequests: number;
}

const kpiCards = [
  {
    key: 'wineries' as const,
    label: 'Wineries',
    icon: FiIcons.FiBriefcase,
    accent: 'bg-primary-50 text-primary-700 ring-primary-100',
  },
  {
    key: 'users' as const,
    label: 'Users',
    icon: FiIcons.FiUsers,
    accent: 'bg-secondary-50 text-secondary-700 ring-secondary-100',
  },
  {
    key: 'newContactRequests' as const,
    label: 'New contact requests',
    icon: FiIcons.FiMail,
    accent: 'bg-accent-50 text-accent-700 ring-accent-100',
  },
];

const Overview: React.FC = () => {
  const [overview, setOverview] = useState<PlatformOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadOverview = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await api.get<PlatformOverview>('/api/platform/overview');
        setOverview(res.data);
      } catch {
        setError('Unable to load platform overview.');
      } finally {
        setLoading(false);
      }
    };

    loadOverview();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-[3px] border-neutral-200 border-t-primary-500" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-neutral-900 tracking-tight">Overview</h1>
        <p className="mt-1 text-sm text-neutral-500">Platform-wide counts at a glance.</p>
      </div>

      {error && (
        <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {kpiCards.map((card) => {
          const Icon = card.icon;
          const value = overview?.[card.key];

          return (
            <div
              key={card.key}
              className="rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm"
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-neutral-500">{card.label}</p>
                  <p className="mt-2 text-3xl font-semibold text-neutral-900 tabular-nums">
                    {value ?? '—'}
                  </p>
                </div>
                <div className={`rounded-xl p-2.5 ring-1 ${card.accent}`}>
                  {Icon({ size: 20 })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Overview;
